import { v1 as uuid } from 'uuid';
import { isEmpty } from 'lodash';
import { getPromptFromHtml } from 'components/FormattedInput/helpers';
import { ANSWER_DEFAULT } from 'containers/QuestionnairePage/constants';
import {
  isItemFromCurrentRevision,
  isItemInsideGroup,
} from 'containers/Permissions/helpers';
import {
  BIPOLAR_DEFAULT_COUNT_STATEMENTS,
  BIPOLAR_DEFAULT_COUNT_SCALEPOINTS,
  BIPOLAR_SLIDER_DEFAULT_COUNT_SCALEPOINTS,
  BIPOLAR_STYLES,
} from './constants';

export const getEmptyAnswer = () => ({
  body: '',
  refId: uuid(),
  type: ANSWER_DEFAULT,
});

export const getEmptyBipolarAnswer = (answerRefId) => ({
  refId: uuid(),
  answerRefId,
  beforeLabel: '',
  afterLabel: '',
});

export const isMatrixAnswerEmpty = (matrixAnswer) =>
  isEmpty(getPromptFromHtml(matrixAnswer.beforeLabel)) ||
  isEmpty(getPromptFromHtml(matrixAnswer.afterLabel));

export const getEmptyBipolarColumn = (questionRefId) => ({
  refId: uuid(),
  questionRefId,
});

export const getEmptyBipolarLabel = () => ({
  refId: uuid(),
  label: '',
});

export const answersToBatchText = (rows = [], shouldCleanHtml) => {
  const rowsText = rows.reduce((text, row) => {
    const cleanBeforeLabel = shouldCleanHtml
      ? getPromptFromHtml(row.beforeLabel)
      : row.beforeLabel;
    let body = (cleanBeforeLabel || '').trim();
    if (row.afterLabel) {
      const cleanAfterLabel = shouldCleanHtml
        ? getPromptFromHtml(row.afterLabel)
        : row.afterLabel;
      body += ` | ${cleanAfterLabel}`;
    } else if (row.beforeLabel) {
      body += ' | ';
    }
    if (!body) {
      return text;
    }
    return `${text}${body}\n`;
  }, '');

  return {
    rowsText: rowsText.trim(),
  };
};

export const getNColumnsAndAnswers = ({
  matrixAnswers,
  columns,
  answers,
  questionRefId,
  answerCount,
  columnCount,
}) => {
  let needsUpdating = false;
  const newAnswers = [...answers];
  const newMatrixAnswers = [...matrixAnswers];
  const newColumns = [...columns];
  const numberOfAnswersToAdd = answerCount - matrixAnswers.length;
  if (numberOfAnswersToAdd > 0) {
    needsUpdating = true;
    for (let i = 0; i < numberOfAnswersToAdd; i += 1) {
      const answer = getEmptyAnswer();
      newAnswers.push(answer);
      newMatrixAnswers.push(getEmptyBipolarAnswer(answer.refId));
    }
  }

  const numberOfColumnsToAdd = columnCount - columns.length;
  if (numberOfColumnsToAdd > 0) {
    needsUpdating = true;
    for (let i = 0; i < numberOfColumnsToAdd; i += 1) {
      newColumns.push(getEmptyBipolarColumn(questionRefId));
    }
  }

  return {
    needsUpdating,
    newAnswers,
    newMatrixAnswers,
    newColumns,
  };
};

export function getDefaultColumnsAndAnswers({
  matrixAnswers,
  columns,
  answers,
  questionRefId,
  bipolarType,
}) {
  const isSliderBipolar = BIPOLAR_STYLES.SLIDER === bipolarType;
  const defaultStatementCount = BIPOLAR_DEFAULT_COUNT_STATEMENTS;
  const defaultScalepointCount = isSliderBipolar
    ? BIPOLAR_SLIDER_DEFAULT_COUNT_SCALEPOINTS
    : BIPOLAR_DEFAULT_COUNT_SCALEPOINTS;

  return getNColumnsAndAnswers({
    matrixAnswers,
    columns,
    answers,
    questionRefId,
    bipolarType,
    answerCount: defaultStatementCount,
    columnCount: defaultScalepointCount,
  });
}

export const isCarriedForward = (answer) => Boolean(answer.isCarriedForward);

export const canRemoveItem = ({ isUnderEdit, itemToCheck }) => {
  if (isCarriedForward(itemToCheck)) {
    return false;
  }
  if (isUnderEdit && !isItemFromCurrentRevision({ itemToCheck })) {
    return false;
  }

  return true;
};

export const canAddStatementItem = ({ isUnderEdit, itemToCheck }) => {
  if (!isUnderEdit) {
    return true;
  }

  if (isItemFromCurrentRevision({ itemToCheck })) {
    return true;
  }

  if (!isItemInsideGroup({ itemToCheck })) {
    return true;
  }

  return false;
};

export const canAddScalepointItem = ({ isUnderEdit, itemToCheck }) => {
  if (!isUnderEdit) {
    return true;
  }

  if (isItemFromCurrentRevision({ itemToCheck })) {
    return true;
  }

  return false;
};
